import { ItemCost, ItemUnit } from "@forgepoint/shared";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { CurrencyInput } from "@/components/CurrencyInputDecimal";
import { createId } from "@paralleldrive/cuid2";
import { ItemCostLastUpdated } from "./ItemCostLastUpdated";
import { useItemSettingsStore } from "@/store/itemSettingsStore";

interface ItemCostBreakProps {
  itemUnit: ItemUnit;
  itemId?: string;
  costBreaks: ItemCost[];
  enableCostBreaks: boolean;
  onCostBreaksChange: (costBreaks: ItemCost[]) => void;
  onEnableCostBreaksChange: (enabled: boolean) => void;
  switchId?: string;
  costInputId?: string;
}

/**
 * Component that manages an item's optional unit-cost source.
 * Handles both simple single-cost mode and quantity-based pricing mode, hiding
 * "Add Break" once the org's maximum cost breaks is reached.
 * @param props - Component props including itemUnit, costBreaks, and handlers
 * @returns Cost break editor UI
 */
export function ItemCostBreak({
  itemUnit,
  itemId,
  costBreaks,
  enableCostBreaks,
  onCostBreaksChange,
  onEnableCostBreaksChange,
  switchId = "enable-cost-breaks",
  costInputId = "simple-cost",
}: ItemCostBreakProps) {
  const maxCostBreaks = useItemSettingsStore(
    (state) => state.settings?.maxPurchaseCostBreaks ?? null,
  );
  const canAddCostBreak =
    maxCostBreaks === null || costBreaks.length < maxCostBreaks;

  const handleAddCostBreak = () => {
    const newCostBreak: ItemCost = {
      id: createId(),
      itemId: itemId || "",
      minQty: 0,
      unitCost: "",
    };
    onCostBreaksChange([...costBreaks, newCostBreak]);
  };

  const handleRemoveCostBreak = (index: number) => {
    onCostBreaksChange(costBreaks.filter((_, i) => i !== index));
  };

  const handleCostBreakChange = (
    index: number,
    field: "minQty" | "unitCost",
    value: string | number | null,
  ) => {
    const updatedCostBreaks = [...costBreaks];
    if (field === "minQty") {
      const numValue =
        typeof value === "number" ? value : parseInt(value as string, 10);
      updatedCostBreaks[index] = {
        ...updatedCostBreaks[index],
        minQty: isNaN(numValue) ? 0 : numValue,
      };
    } else {
      updatedCostBreaks[index] = {
        ...updatedCostBreaks[index],
        unitCost: (value as string) || "",
      };
    }
    onCostBreaksChange(updatedCostBreaks);
  };

  const handleToggleCostBreaks = (checked: boolean) => {
    onEnableCostBreaksChange(checked);
    if (!checked) {
      const baseCostBreak =
        costBreaks.find((cb) => cb.minQty === 1) || costBreaks[0];
      if (baseCostBreak) {
        onCostBreaksChange([{ ...baseCostBreak, minQty: 1 }]);
      } else {
        onCostBreaksChange([
          {
            id: createId(),
            itemId: itemId || "",
            minQty: 1,
            unitCost: "",
          },
        ]);
      }
    } else if (costBreaks.length === 0) {
      handleAddCostBreak();
    }
  };

  const handleSimpleCostChange = (value: string | null) => {
    if (costBreaks.length === 0) {
      onCostBreaksChange([
        {
          id: createId(),
          itemId: itemId || "",
          minQty: 1,
          unitCost: value || "",
        },
      ]);
    } else {
      onCostBreaksChange([
        {
          ...costBreaks[0],
          minQty: 1,
          unitCost: value || "",
        },
      ]);
    }
  };

  return (
    <div className="divide-y">
      <div className="py-2.5">
        <div className="flex items-center justify-between gap-4">
          <Label
            htmlFor={switchId}
            className="text-sm font-medium leading-tight min-w-0 cursor-pointer"
          >
            Quantity-based pricing
          </Label>
          <div className="w-[200px] shrink-0 flex justify-end">
            <Switch
              id={switchId}
              checked={enableCostBreaks}
              onCheckedChange={handleToggleCostBreaks}
            />
          </div>
        </div>
      </div>

      {!enableCostBreaks ? (
        <div
          className="py-2.5"
          data-field-anchor={`costBreaks.${costBreaks[0]?.minQty ?? 1}`}
        >
          <div className="grid grid-cols-[minmax(0,1fr)_200px] items-center gap-x-4 gap-y-0.5">
            <Label
              htmlFor={costInputId}
              className="text-sm font-medium leading-tight min-w-0"
            >
              Unit Cost
            </Label>
            <div>
              <CurrencyInput
                id={costInputId}
                value={costBreaks[0]?.unitCost || ""}
                onChange={handleSimpleCostChange}
                decimals={3}
              />
            </div>
            {itemId ? <ItemCostLastUpdated itemId={itemId} /> : null}
          </div>
        </div>
      ) : (
        <div className="py-2.5 space-y-2">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-0.5">
            <Label className="text-sm font-medium leading-tight">
              Quantity-Based Cost Breaks
            </Label>
            {canAddCostBreak ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddCostBreak}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Break
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">
                Up to {maxCostBreaks} breaks
              </span>
            )}
            {itemId ? <ItemCostLastUpdated itemId={itemId} /> : null}
          </div>
          <div className="space-y-2">
            {costBreaks.map((costBreak, index) => (
              <div
                key={costBreak.id}
                className="flex items-center gap-2"
                data-field-anchor={`costBreaks.${costBreak.minQty}`}
              >
                <div className="flex-1">
                  <Input
                    type="number"
                    placeholder={`Min ${itemUnit}`}
                    value={costBreak.minQty || ""}
                    onChange={(e) =>
                      handleCostBreakChange(index, "minQty", e.target.value)
                    }
                  />
                </div>
                <div className="flex-1">
                  <CurrencyInput
                    placeholder="Cost"
                    value={costBreak.unitCost}
                    onChange={(value) =>
                      handleCostBreakChange(index, "unitCost", value || "")
                    }
                    decimals={3}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveCostBreak(index)}
                  disabled={costBreaks.length === 1}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
